import React from 'react';

const TimeSlider = ({ 
  availableYears, 
  currentYear, 
  onYearChange, 
  isLoading, 
  compact = false, 
  semiTransparent = false,
  mobile = false 
}) => {
  const handleSliderChange = (e) => {
    const yearIndex = parseInt(e.target.value);
    onYearChange(availableYears[yearIndex]);
  };

  const currentYearIndex = availableYears.indexOf(currentYear);
  const progressPercentage = (currentYearIndex / (availableYears.length - 1)) * 100;

  // MOBILE: Detect if we're on mobile
  const isMobile = mobile || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

  // MOBILE: Enhanced mobile styling
  const containerStyle = {
    background: isMobile && semiTransparent ? 
      'rgba(15, 20, 25, 0.7)' : 
      semiTransparent ? 'rgba(15, 20, 25, 0.15)' : 'rgba(15, 20, 25, 0.9)',
    backdropFilter: isMobile ? 'blur(16px)' : semiTransparent ? 'blur(4px)' : 'blur(20px)',
    borderRadius: isMobile ? '16px' : compact ? '12px' : '16px',
    padding: isMobile ? '16px 20px' : compact ? '12px 20px' : '16px 24px',
    border: `1px solid ${isMobile ? 'rgba(255, 255, 255, 0.15)' : semiTransparent ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.1)'}`,
    boxShadow: isMobile ? 
      '0 12px 40px rgba(0, 0, 0, 0.6), 0 4px 16px rgba(0, 0, 0, 0.3)' :
      semiTransparent ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 8px 20px rgba(0, 0, 0, 0.4)',
    minWidth: isMobile ? '280px' : compact ? '320px' : '420px',
    maxWidth: isMobile ? '100%' : compact ? '420px' : '560px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif',
    opacity: semiTransparent && !isMobile ? 0.6 : 1,
    transition: isMobile ? 'all 0.3s ease' : semiTransparent ? 'all 0.3s ease, opacity 0.2s ease' : 'all 0.3s ease',
    // MOBILE: Enhanced touch-friendly design
    touchAction: 'manipulation',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent'
  };

  // MOBILE: Enhanced hover/touch effects
  const handleMouseEnter = (e) => {
    if (semiTransparent && !isMobile) {
      e.currentTarget.style.opacity = '0.95';
      e.currentTarget.style.background = 'rgba(15, 20, 25, 0.7)';
      e.currentTarget.style.backdropFilter = 'blur(12px)';
    }
  };

  const handleMouseLeave = (e) => {
    if (semiTransparent && !isMobile) {
      e.currentTarget.style.opacity = '0.6';
      e.currentTarget.style.background = 'rgba(15, 20, 25, 0.15)';
      e.currentTarget.style.backdropFilter = 'blur(4px)';
    }
  };

  // MOBILE: Touch handlers for better mobile experience
  const handleTouchStart = (e) => {
    if (isMobile) {
      e.currentTarget.style.transform = 'scale(0.98)';
      e.currentTarget.style.transition = 'transform 0.1s ease';
    }
  };

  const handleTouchEnd = (e) => {
    if (isMobile) {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.transition = 'all 0.3s ease';
    }
  };

  // Get display year (first year only)
  const getDisplayYear = (year) => {
    if (year.includes('-')) {
      return year.split('-')[0]; // Return only the first year
    }
    return year;
  };

  // MOBILE: Quick year selection buttons for mobile
  const renderMobileYearButtons = () => {
    if (!isMobile) return null;

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '12px',
        gap: '4px'
      }}>
        {availableYears.map((year, index) => (
          <button
            key={year}
            onClick={() => onYearChange(year)}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '8px 4px',
              background: index === currentYearIndex ? 
                'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' : 
                'rgba(255, 255, 255, 0.08)',
              border: `1px solid ${index === currentYearIndex ? '#3B82F6' : 'rgba(255, 255, 255, 0.12)'}`,
              borderRadius: '8px',
              color: index === currentYearIndex ? 'white' : 'rgba(255, 255, 255, 0.7)',
              fontSize: '11px',
              fontWeight: index === currentYearIndex ? '600' : '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isLoading ? 0.5 : 1,
              boxShadow: index === currentYearIndex ? 
                '0 4px 12px rgba(59, 130, 246, 0.3)' : 
                '0 2px 4px rgba(0, 0, 0, 0.1)',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent'
            }}
            onTouchStart={(e) => {
              if (index !== currentYearIndex && !isLoading) {
                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                e.target.style.transform = 'scale(0.95)';
              }
            }}
            onTouchEnd={(e) => {
              if (index !== currentYearIndex && !isLoading) {
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            {getDisplayYear(year)}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div 
      style={containerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* MOBILE: Quick year buttons */}
      {renderMobileYearButtons()}
      
      {/* Current Year Display */}
      <div style={{
        textAlign: 'center',
        marginBottom: isMobile ? '14px' : compact ? '16px' : '20px'
      }}>
        <div style={{
          fontSize: isMobile ? '22px' : compact ? '20px' : '24px',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          lineHeight: '1',
          marginBottom: '4px',
          // MOBILE: Enhanced text shadow for better readability
          textShadow: isMobile ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none'
        }}>
          {getDisplayYear(currentYear)}
        </div>
        <div style={{
          fontSize: isMobile ? '11px' : compact ? '10px' : '12px',
          color: isMobile ? 'rgba(255, 255, 255, 0.7)' : semiTransparent ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.5)',
          fontWeight: '400'
        }}>
          Academic Year {currentYear.includes('-') ? currentYear : `${(parseInt(currentYear.split('-')[0] || currentYear) - 1)}-${currentYear.slice(-2)}`}
        </div>
      </div>

      {/* Slider */}
      <div style={{ 
        position: 'relative', 
        width: '100%', 
        marginBottom: isMobile ? '14px' : compact ? '12px' : '16px' 
      }}>
        <div style={{
          width: '100%',
          height: isMobile ? '6px' : '4px', // Thicker on mobile for better touch
          background: isMobile ? 
            'rgba(255, 255, 255, 0.15)' :
            semiTransparent ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.12)',
          borderRadius: isMobile ? '3px' : '2px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Progress track */}
          <div style={{
            width: `${progressPercentage}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
            borderRadius: isMobile ? '3px' : '2px',
            transition: 'width 0.3s ease',
            boxShadow: isMobile ? 
              '0 0 12px rgba(59, 130, 246, 0.6)' : 
              '0 0 8px rgba(59, 130, 246, 0.4)'
          }}></div>
        </div>
        
        <input
          type="range"
          min="0"
          max={availableYears.length - 1}
          value={currentYearIndex}
          onChange={handleSliderChange}
          disabled={isLoading}
          style={{
            position: 'absolute',
            top: isMobile ? '-10px' : '-8px',
            left: 0,
            width: '100%',
            height: isMobile ? '26px' : '20px', // Larger touch target on mobile
            background: 'transparent',
            outline: 'none',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.4 : 1,
            WebkitAppearance: 'none',
            appearance: 'none',
            // MOBILE: Better touch handling
            touchAction: 'manipulation'
          }}
        />
      </div>

      {/* Year markers - Hidden on mobile to save space */}
      {!isMobile && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingLeft: '2px',
          paddingRight: '2px'
        }}>
          {availableYears.map((year, index) => (
            <div
              key={year}
              style={{
                fontSize: compact ? '10px' : '11px',
                color: index === currentYearIndex ? '#60A5FA' : 
                       semiTransparent ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.4)',
                fontWeight: index === currentYearIndex ? '600' : '400',
                cursor: 'pointer',
                padding: compact ? '3px 5px' : '4px 6px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                background: index === currentYearIndex ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                whiteSpace: 'nowrap'
              }}
              onClick={() => {
                onYearChange(year);
              }}
              onMouseEnter={(e) => {
                if (index !== currentYearIndex) {
                  e.target.style.color = semiTransparent ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.7)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (index !== currentYearIndex) {
                  e.target.style.color = semiTransparent ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.4)';
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {getDisplayYear(year)}
            </div>
          ))}
        </div>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div style={{
          marginTop: isMobile ? '10px' : compact ? '8px' : '12px',
          textAlign: 'center',
          fontSize: isMobile ? '11px' : compact ? '10px' : '11px',
          color: isMobile ? 
            'rgba(255, 255, 255, 0.7)' :
            semiTransparent ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <div style={{
            width: isMobile ? '14px' : '12px',
            height: isMobile ? '14px' : '12px',
            border: `${isMobile ? '2px' : '1.5px'} solid rgba(255, 255, 255, 0.2)`,
            borderTop: `${isMobile ? '2px' : '1.5px'} solid #3B82F6`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ fontWeight: '400' }}>
            Loading {currentYear}...
          </span>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* MOBILE: Enhanced slider thumb styles */
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: ${isMobile ? '24px' : '20px'};
          width: ${isMobile ? '24px' : '20px'};
          border-radius: 50%;
          background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
          cursor: pointer;
          box-shadow: ${isMobile ? 
            '0 4px 12px rgba(59, 130, 246, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.2)' :
            '0 3px 8px rgba(59, 130, 246, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)'
          };
          border: ${isMobile ? '3px solid rgba(255, 255, 255, 0.95)' : '2px solid rgba(255, 255, 255, 0.9)'};
          transition: all 0.2s ease;
          position: relative;
          z-index: 10;
        }
        
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(${isMobile ? '1.15' : '1.1'});
          box-shadow: ${isMobile ?
            '0 6px 20px rgba(59, 130, 246, 0.6), 0 0 0 2px rgba(255, 255, 255, 0.3)' :
            '0 4px 12px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)'
          };
        }
        
        input[type="range"]::-webkit-slider-thumb:active {
          transform: scale(${isMobile ? '1.2' : '1.15'});
          box-shadow: ${isMobile ?
            '0 8px 25px rgba(59, 130, 246, 0.7), 0 0 0 3px rgba(255, 255, 255, 0.4)' :
            '0 6px 16px rgba(59, 130, 246, 0.6), 0 0 0 2px rgba(255, 255, 255, 0.3)'
          };
        }
        
        input[type="range"]::-moz-range-thumb {
          height: ${isMobile ? '24px' : '20px'};
          width: ${isMobile ? '24px' : '20px'};
          border-radius: 50%;
          background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
          cursor: pointer;
          box-shadow: ${isMobile ? 
            '0 4px 12px rgba(59, 130, 246, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.2)' :
            '0 3px 8px rgba(59, 130, 246, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)'
          };
          border: ${isMobile ? '3px solid rgba(255, 255, 255, 0.95)' : '2px solid rgba(255, 255, 255, 0.9)'};
          transition: all 0.2s ease;
        }

        /* MOBILE: Better touch target for range input */
        @media (max-width: 768px) {
          input[type="range"] {
            height: 30px !important;
            top: -12px !important;
          }
        }

        /* Disable text selection on mobile */
        @media (hover: none) and (pointer: coarse) {
          * {
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            user-select: none;
          }
        }
      `}</style>
    </div>
  );
};

export default TimeSlider;